import { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { deleteStatus } from 'mastodon/actions/statuses';
import { useAppDispatch } from 'mastodon/store';

import type { BaseConfirmationModalProps } from './confirmation_modal';
import { ConfirmationModal } from './confirmation_modal';

const messages = defineMessages({
  deleteAndRedraftTitle: {
    id: 'confirmations.redraft.title',
    defaultMessage: 'Delete & redraft post?',
  },
  deleteAndRedraftMessage: {
    id: 'confirmations.redraft.message',
    defaultMessage:
      'Are you sure you want to delete this status and re-draft it? Favorites and boosts will be lost, and replies to the original post will be orphaned.',
  },
  deleteAndRedraftConfirm: {
    id: 'confirmations.redraft.confirm',
    defaultMessage: 'Delete & redraft',
  },
  deleteTitle: {
    id: 'confirmations.delete.title',
    defaultMessage: 'Delete post?',
  },
  deleteMessage: {
    id: 'confirmations.delete.message',
    defaultMessage: 'Are you sure you want to delete this status?',
  },
  deleteConfirm: {
    id: 'confirmations.delete.confirm',
    defaultMessage: 'Delete',
  },
  deleteCommentTitle: {
    id: 'confirmations.delete.comment.title',
    defaultMessage: 'Delete comment?',
  },
  deleteCommentMessage: {
    id: 'confirmations.delete.comment.message',
    defaultMessage: 'Are you sure you want to delete this comment?',
  },
  deleteCommentConfirm: {
    id: 'confirmations.delete.comment.confirm',
    defaultMessage: 'Delete comment',
  },
});

export const ConfirmDeleteStatusModal: React.FC<
  {
    statusId: string;
    withRedraft: boolean;
    isComment: boolean;
    statusOriginId: string | undefined
  } & BaseConfirmationModalProps
> = ({ statusId, withRedraft, isComment, statusOriginId, onClose }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const onConfirm = useCallback(() => {
    dispatch(deleteStatus(statusId, withRedraft, statusOriginId));
  }, [dispatch, statusId, withRedraft, statusOriginId]);

  let title;
  let message;
  let confirm;

  if (isComment) {
    title = messages.deleteCommentTitle;
    message = messages.deleteCommentMessage;
    confirm = messages.deleteCommentConfirm;
  } else if (withRedraft) {
    title = messages.deleteAndRedraftTitle;
    message = messages.deleteAndRedraftMessage;
    confirm = messages.deleteAndRedraftConfirm;
  } else {
    title = messages.deleteTitle;
    message = messages.deleteMessage;
    confirm = messages.deleteConfirm;
  }

  return (
    <ConfirmationModal
      title={intl.formatMessage(title)}
      message={intl.formatMessage(message)}
      confirm={intl.formatMessage(confirm)}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
};
